/**
 * The % of a compare condition is OPT-IN. Off (no percent stored) the
 * operator compares the two metrics directly and this renders one dashed %
 * button; clicking it activates the tolerance at 100 (= "the same level").
 * Active, it renders the percent input plus a lit "% of" button — hovering
 * either shows exactly how the level is calculated (with the campaign's real
 * numbers when the live preview has them); clicking the button switches back
 * to the direct comparison.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Percent } from 'lucide-react';

import { SHOW_COMPARE_PERCENT } from '../../../../compare/compare-flags';
import ThresholdInput from '../ThresholdInput';

export interface PercentToggleProps {
  percent?: number;
  /** Hover text explaining the calculation (real numbers when available). */
  calcTitle: string;
  /** Hover text for the inactive % button. */
  addTitle: string;
  onChange: (percent: number | undefined) => void;
}

const PercentToggle: FC<PercentToggleProps> = ({
  percent,
  calcTitle,
  addTitle,
  onChange,
}) => {
  if (percent === undefined) {
    // The % is currently hidden (see compare-flags) — the direct comparison
    // is the whole feature until the flag flips back.
    if (!SHOW_COMPARE_PERCENT) return null;
    return (
      <button
        type="button"
        onClick={() => onChange(100)}
        title={addTitle}
        aria-label="Add a percent"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
      >
        <Percent className="h-3.5 w-3.5" />
      </button>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1" title={calcTitle}>
      <ThresholdInput
        value={Number.isFinite(percent) ? percent : 100}
        onChange={onChange}
        ariaLabel="Compare percent"
      />
      <button
        type="button"
        onClick={() => onChange(undefined)}
        title={`${calcTitle}\n\nClick to remove the % and compare the metrics directly.`}
        aria-label="Remove the percent"
        className="flex h-8 shrink-0 items-center gap-0.5 rounded-md bg-primary/15 px-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/25"
      >
        <Percent className="h-3 w-3" />
        of
      </button>
    </span>
  );
};

export default PercentToggle;
